/** One Pangram classification window over a span of the scored text. */
export interface AiTextDetectionWindow {
  text: string;
  label: string;
  aiAssistanceScore: number;
  confidence: string;
  startIndex: number;
  endIndex: number;
  wordCount: number;
  tokenLength: number;
  isHumanized: boolean;
  humanizerScore: number;
}

/** Response of the admin-only `detectAiTextForAdmin` callable. */
export interface AiTextDetectionResult {
  model: string;
  version: string;
  text: string;
  wordCount: number;
  durationMs: number;
  headline: string;
  prediction: string;
  predictionShort: string;
  fractionAi: number;
  fractionAiAssisted: number;
  fractionHuman: number;
  numAiSegments: number;
  numAiAssistedSegments: number;
  numHumanSegments: number;
  windows: AiTextDetectionWindow[];
}

export type AuthorshipKind = 'ai' | 'assisted' | 'human';

// Below this the validator says "too short to check"; the tool still scores
// short text but warns that the result is unreliable.
export const RELIABLE_MIN_WORDS = 50;

/**
 * Pangram labels are free-form display strings ("AI-Generated",
 * "Moderately AI-Assisted", "Human Written"), so classify by keyword.
 */
export function authorshipKind(label: string): AuthorshipKind {
  const normalized = label.toLowerCase();
  if (normalized.includes('assist')) return 'assisted';
  if (normalized.includes('human')) return 'human';
  if (/\bai\b/.test(normalized)) return 'ai';
  return 'human';
}

/** The overall verdict follows the largest authorship share. */
export function overallKind(result: Pick<AiTextDetectionResult, 'fractionAi' | 'fractionAiAssisted' | 'fractionHuman'>): AuthorshipKind {
  const { fractionAi, fractionAiAssisted, fractionHuman } = result;
  if (fractionAi >= fractionAiAssisted && fractionAi >= fractionHuman && fractionAi > 0) return 'ai';
  if (fractionAiAssisted >= fractionHuman && fractionAiAssisted > 0) return 'assisted';
  return 'human';
}

export interface HighlightSegment {
  text: string;
  /** Index into `windows`, or null for text no window covers. */
  windowIndex: number | null;
}

/**
 * Split `text` into spans covered by each window, plus uncovered gaps, so
 * the view can render the original text with per-window highlighting. If
 * the indices don't line up with `text` (out of range or overlapping), fall
 * back to the windows' own text so nothing is mis-highlighted.
 */
export function buildHighlightSegments(text: string, windows: AiTextDetectionWindow[]): HighlightSegment[] {
  const ordered = windows
    .map((window, index) => ({ window, index }))
    .sort((a, b) => a.window.startIndex - b.window.startIndex);

  const aligned = ordered.every(({ window }, position) => (
    window.startIndex >= 0
    && window.endIndex > window.startIndex
    && window.endIndex <= text.length
    && (position === 0 || window.startIndex >= ordered[position - 1].window.endIndex)
  ));

  if (!aligned) {
    return windows.map((window, index) => ({ text: window.text, windowIndex: index }));
  }

  const segments: HighlightSegment[] = [];
  let cursor = 0;
  for (const { window, index } of ordered) {
    if (window.startIndex > cursor) {
      segments.push({ text: text.slice(cursor, window.startIndex), windowIndex: null });
    }
    segments.push({ text: text.slice(window.startIndex, window.endIndex), windowIndex: index });
    cursor = window.endIndex;
  }
  if (cursor < text.length) segments.push({ text: text.slice(cursor), windowIndex: null });
  return segments;
}

export function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}
